import React, { memo, useMemo, Children } from 'react';
import { Modal, Tag } from 'antd';
import {
  UserOutlined,
  MedicineBoxOutlined,
  FileTextOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import styles from './style.module.css';
import {
  TASK_STATUS_CONFIG,
  TaskStatusInfo,
} from '../../../models/CallAttempt/CallAttempt';
import { TaskStatusIcon } from './StatusIcon';
import { dateTime } from '../../../utils/formatDate';
import { AudioPlayerGroup } from './AudioPlayer/AudioPlayerGroup';
import { CallAttemptCard } from './CallAttemptCard/CallAttemptCard';
import { taskResultLabel } from '../../../models/enums/TaskResult';
import { taskStatusLabel } from '../../../models/enums/TaskProcessingStatus';
import { CallDetailsViewModel } from '../../../models/Task/callDetailsViewModel';

const NO_DATA = 'Нет данных';

const valueOrNoData = (value: string | null | undefined) =>
  value?.trim() || NO_DATA;

const formatDateTimeValue = (value: string | null | undefined) =>
  value ? dateTime(value) : NO_DATA;

const directionLabel = (direction: 'outbound' | 'inbound' | 'both') => {
  if (direction === 'inbound') return 'входящий';
  if (direction === 'outbound') return 'исходящий';
  return 'исходящий/входящий';
};

const attemptCountLabel = (count: number): string => {
  const lastTwo = count % 100;
  const lastDigit = count % 10;
  if (lastTwo >= 11 && lastTwo <= 14) return `${count} попыток`;
  if (lastDigit === 1) return `${count} попытка`;
  if (lastDigit >= 2 && lastDigit <= 4) return `${count} попытки`;
  return `${count} попыток`;
};

export const JOURNAL_TASK_STATUS_CONFIG: Record<string, TaskStatusInfo> = {
  NotProcessed: { label: 'В очереди', color: '#8C8C8C' },
  not_processed: { label: 'В очереди', color: '#8C8C8C' },
};

interface DetailRowProps {
  label: string;
  value: string | null | undefined;
}

const DetailRow = ({ label, value }: DetailRowProps) => (
  <>
    <span className={styles.infoLabel}>{label}:</span>
    <span
      className={
        value?.trim()
          ? styles.infoValue
          : `${styles.infoValue} ${styles.noData}`
      }
    >
      {valueOrNoData(value)}
    </span>
  </>
);

interface SectionHeaderProps {
  icon: React.ReactNode;
  title: string;
}

const SectionHeader = ({ icon, title }: SectionHeaderProps) => (
  <h4 className={styles.sectionTitle}>
    <span className={styles.sectionIcon}>{icon}</span>
    {title}
  </h4>
);

interface CallDetailsModalProps {
  open: boolean;
  onClose: () => void;
  taskStatus: string;
  patientPhone: string;
  patientFullName: string | null;
  // Outgoing-specific
  clinicName: string | null;
  appointmentDate: string | null;
  doctorName: string | null;
  doctorSpecialization: string | null;
  // Incoming-specific
  incomingClinicName?: string | null;
  incomingStage?: string | null;
  incomingCallDate?: string | null;
  children: React.ReactNode;
  callType: 'outgoing' | 'incoming';
  taskResult?: string | null;
  resultLabelByValue?: Readonly<Record<string, string>>;
  scenario?: string | null;
  details?: CallDetailsViewModel | null;
  isLoadingDetails?: boolean;
  detailsError?: string | null;
  /**
   * Per-status label/color overrides, merged on top of the shared
   * `TASK_STATUS_CONFIG`. Used by the survey journal where the same
   * ResultsTypes values carry product-specific wording.
   */
  statusConfigOverride?: Record<string, TaskStatusInfo>;
}

export const CallDetailsModal = memo(
  ({
    open,
    onClose,
    taskStatus,
    patientPhone,
    patientFullName,
    clinicName,
    appointmentDate,
    doctorName,
    doctorSpecialization,
    incomingClinicName,
    incomingStage,
    incomingCallDate,
    children,
    callType,
    statusConfigOverride,
    taskResult,
    resultLabelByValue = {},
    scenario,
    details,
    isLoadingDetails,
    detailsError,
  }: CallDetailsModalProps) => {
    const getAppointmentDateDisplay = () => {
      if (!appointmentDate) {
        return NO_DATA;
      }
      const stripped = appointmentDate.replace(/<[^>]*>/g, ' ').trim();
      const parts = stripped.split(/\s+/).filter(Boolean);
      const datePart = parts[0] ?? '';
      const timePart = parts[1] ?? '';
      if (!datePart) {
        return NO_DATA;
      }
      return [datePart, timePart].filter(Boolean).join(' ');
    };

    const resolvedStatus = details?.header.state ?? taskStatus;
    const resolvedResult = details?.header.result ?? taskResult;
    const resolvedScenarioLabel = details?.scenario.label ?? scenario;
    const resolvedDirection =
      callType === 'incoming'
        ? 'входящий'
        : details
          ? directionLabel(details.scenario.directionSupport)
          : 'исходящий';
    const resolvedPatientPhone = details?.patient.phone ?? patientPhone;
    const resolvedPatientFullName =
      details?.patient.fullName ?? patientFullName;

    const statusConfig =
      statusConfigOverride?.[resolvedStatus] ??
      TASK_STATUS_CONFIG[resolvedStatus];
    const statusLabel = statusConfig?.label ?? taskStatusLabel(resolvedStatus);
    const statusColor = statusConfig?.color ?? '#8C8C8C';

    const badgeStyle = useMemo(
      () => ({
        backgroundColor: `${statusColor}14`,
        color: statusColor,
      }),
      [statusColor],
    );

    const stageLabel = incomingStage
      ? (TASK_STATUS_CONFIG[incomingStage]?.label ??
        taskStatusLabel(incomingStage))
      : null;

    const formattedCallDate = incomingCallDate
      ? dateTime(incomingCallDate)
      : null;

    const resolvedTaskResultLabel = resolvedResult
      ? (resultLabelByValue[resolvedResult] ?? taskResultLabel(resolvedResult))
      : null;

    const detailsTitle = [statusLabel, resolvedTaskResultLabel]
      .filter(Boolean)
      .join(' · ');
    const attemptCount =
      details?.callAttempts && details.callAttempts.length > 0
        ? details.callAttempts.length
        : Children.toArray(children).filter(
            (child) =>
              React.isValidElement(child) && child.type === CallAttemptCard,
          ).length;

    const renderLegacyDetails = () => (
      <>
        <h4 className={styles.sectionTitle}>Информация о пациенте</h4>
        <div className={styles.infoGrid}>
          <DetailRow label="Телефон" value={resolvedPatientPhone} />
          <DetailRow label="ФИО" value={resolvedPatientFullName} />
        </div>

        {callType === 'outgoing' && (
          <>
            <h4 className={styles.sectionTitle}>Информация о записи</h4>
            <div className={styles.infoGrid}>
              <DetailRow label="Клиника" value={clinicName} />
              <DetailRow
                label="Дата приема"
                value={getAppointmentDateDisplay()}
              />
              <DetailRow label="Врач" value={doctorName} />
              <DetailRow label="Специализация" value={doctorSpecialization} />
            </div>
          </>
        )}

        {callType === 'incoming' && (
          <>
            <h4 className={styles.sectionTitle}>Информация о звонке</h4>
            <div className={styles.infoGrid}>
              <DetailRow label="Клиника" value={incomingClinicName} />
              <DetailRow label="Ступень" value={stageLabel} />
              <DetailRow label="Дата звонка" value={formattedCallDate} />
            </div>
          </>
        )}
      </>
    );

    const renderDetailsModel = (model: CallDetailsViewModel) => (
      <>
        {!model.isSupported && (
          <div className={styles.noticeBlock}>
            Детализация для сценария недоступна
          </div>
        )}

        {detailsError && (
          <div className={styles.errorBlock}>{detailsError}</div>
        )}

        <SectionHeader icon={<UserOutlined />} title="Информация о пациенте" />
        <div className={styles.infoGrid}>
          <DetailRow label="Телефон" value={model.patient.phone} />
          <DetailRow label="ФИО" value={model.patient.fullName} />
          {model.scenario.group === 'informing' && (
            <DetailRow
              label="Медицинская организация"
              value={model.patient.clinicName}
            />
          )}
        </div>

        {model.scenario.group === 'slot' && (
          <>
            <SectionHeader
              icon={<MedicineBoxOutlined />}
              title="Информация о записи"
            />
            <div className={styles.infoGrid}>
              <DetailRow
                label="Медицинская организация"
                value={model.appointment?.clinicName}
              />
              <DetailRow
                label="Дата и время приема"
                value={formatDateTimeValue(model.appointment?.begin)}
              />
              <DetailRow
                label="Специализация"
                value={model.appointment?.specializationName}
              />
              <DetailRow label="Врач" value={model.appointment?.doctorName} />
            </div>
          </>
        )}

        {model.scenario.group === 'slot' && model.additional?.paymentType && (
          <>
            <SectionHeader icon={<FileTextOutlined />} title="Дополнительно" />
            <div className={styles.infoGrid}>
              <DetailRow
                label="Тип оплаты"
                value={model.additional.paymentType}
              />
            </div>
          </>
        )}

        {model.scenario.group === 'informing' &&
          model.additional?.pathogens && (
            <>
              <SectionHeader
                icon={<FileTextOutlined />}
                title="Дополнительно"
              />
              <div className={styles.infoGrid}>
                <DetailRow
                  label="Возбудители"
                  value={model.additional.pathogens}
                />
              </div>
            </>
          )}

        {model.scenario.group === 'dispensarization' && (
          <>
            <SectionHeader
              icon={<MedicineBoxOutlined />}
              title="Информация о кампании"
            />
            <div className={styles.infoGrid}>
              <DetailRow
                label="Медицинская организация"
                value={model.campaign?.clinicName}
              />
              <DetailRow
                label="Начало кампании"
                value={formatDateTimeValue(model.campaign?.from)}
              />
              <DetailRow
                label="Название кампании"
                value={model.campaign?.name}
              />
            </div>
          </>
        )}

        {model.scenario.group === 'form' && (
          <>
            <SectionHeader
              icon={<MedicineBoxOutlined />}
              title="Информация о кампании"
            />
            <div className={styles.infoGrid}>
              <DetailRow
                label="Медицинская организация"
                value={model.campaign?.clinicName}
              />
              <DetailRow
                label="Начало кампании"
                value={formatDateTimeValue(model.campaign?.from)}
              />
              <DetailRow
                label="Название кампании"
                value={model.form?.batchName}
              />
              <DetailRow
                label="Идентификатор кампании"
                value={model.form?.batchId}
              />
            </div>

            <SectionHeader
              icon={<FileTextOutlined />}
              title="Информация об анкете"
            />
            <div className={styles.infoGrid}>
              <DetailRow label="Тип анкеты" value={model.scenario.label} />
              <DetailRow label="Шаблон" value={model.form?.templateName} />
            </div>
          </>
        )}
      </>
    );

    return (
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        destroyOnHidden={true}
        width={800}
        className={styles.callDetailsModal}
        title={
          <div className={styles.modalTitle}>
            <div className={styles.modalTitleRow}>
              {detailsTitle || 'Детали задания'}
            </div>
            {resolvedScenarioLabel && (
              <div className={styles.modalSubtitle}>
                Сценарий: {resolvedScenarioLabel} ({resolvedDirection})
              </div>
            )}
          </div>
        }
      >
        {isLoadingDetails ? (
          <div className={styles.noticeBlock}>Загрузка деталей...</div>
        ) : details ? (
          renderDetailsModel(details)
        ) : (
          renderLegacyDetails()
        )}

        <div className={styles.historyHeader}>
          <h2>История звонков</h2>
          {callType !== 'incoming' && attemptCount > 0 ? (
            <Tag className={styles.attemptCountChip}>
              {attemptCountLabel(attemptCount)}
            </Tag>
          ) : null}
        </div>
        <AudioPlayerGroup>{children}</AudioPlayerGroup>
      </Modal>
    );
  },
);

CallDetailsModal.displayName = 'CallDetailsModal';
